import { useNotifications } from '../contexts/NotificationContext';
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation } from 'react-router-dom';
import { useLoading } from '../contexts/LoadingContext';

export const backendURL: string = 'https://api.targetcalculator.pl';
// export const backendURL: string = 'http://192.168.50.237:8080';

interface ApiError {
    status: number;
    error: string;
    message: string;
}

export class ApiRequestError extends Error {
    readonly status: number;
    readonly code: string;

    constructor(error: ApiError) {
        super(error.message);
        this.name = 'ApiRequestError';
        this.status = error.status;
        this.code = error.error;
    }
}

class UnauthorizedError extends ApiRequestError {
    constructor(error: ApiError) {
        super(error);
        this.name = 'UnauthorizedError';
    }
}

interface ApiFetchOptions extends RequestInit {
    includeAuth?: boolean;
    showErrorToast?: boolean;
}

async function apiFetch<T = any>(url: string, options: ApiFetchOptions = {}, token?: string | null): Promise<T> {
    const { includeAuth = false, showErrorToast = true, ...fetchOptions } = options;

    const headers = new Headers(fetchOptions.headers);
    headers.set('Content-Type', 'application/json');

    if (includeAuth && token) {
        headers.set('Authorization', token);
    }

    try {
        const response = await fetch(url, {
            ...fetchOptions,
            headers,
        });

        if (!response.ok) {
            const errorData: ApiError = await response.json().catch(() => ({
                status: response.status,
                error: 'UNKNOWN_ERROR',
                message: 'Wystąpił nieznany błąd',
            }));

            if (response.status === 401) {
                throw new UnauthorizedError(errorData);
            }

            throw new ApiRequestError(errorData);
        }

        if (response.headers.get('content-length') === '0' || !response.headers.get('content-type')?.includes('application/json')) {
            return undefined as T;
        }

        return await response.json();
    } catch (error) {
        throw error;
    }
}

export function useApiFetch() {
    const { error: showErrorToast } = useNotifications();
    const { getToken, logout } = useAuth();
    const { startLoading, stopLoading } = useLoading();
    const navigate = useNavigate();
    const location = useLocation();

    return async function <T = any>(url: string, options: ApiFetchOptions = {}): Promise<T> {
        const { showErrorToast: shouldShow = true, ...opts } = options;
        const token = opts.includeAuth ? getToken() : null;

        startLoading();

        try {
            return await apiFetch<T>(url, opts, token);
        } catch (err) {
            console.error('API Error:', err);
            let errorMessage = err.message;

            if (err instanceof UnauthorizedError && location.pathname !== '/sign-in') {
                logout();
                navigate('/sign-in');
            } else if (err instanceof Error && err.message === 'Failed to fetch') {
                errorMessage = 'Nie udało się połączyć z serwerem. Spróbuj ponownie później.';
            }

            if (shouldShow) {
                showErrorToast(errorMessage);
            }

            throw err;
        } finally {
            stopLoading();
        }
    };
}
